import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Filter, ArrowUpDown, Bed, Bath, 
  Square, Search, XCircle, ChevronDown, MapPin,
  Home as HomeIcon, Building, CheckCircle, 
  ChevronLeft, ChevronRight, Castle, HardHat,
  Calendar, Layers, Building2, Map as MapIcon,
  Store, X, Check, ArrowRight, Sparkles,
  TrendingUp
} from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import PropertyCardPro from './profiles/PropertyCardPro';
import { usePropiedades } from '../components/PropiedadesContext';
import LocationSuggestions, { LocationType } from '../components/LocationSuggestions';
import EnhancedLocationSearch from '../components/EnhancedLocationSearch';

/*
Z-index hierarchy:
- 50: Dropdowns and location suggestions
- 40: Filter panel
- 30: Search bar
- 20: Hero content
- 10: Hero banner
- 5: Results container 
*/

// SearchBar Configuration
const PROPERTY_TYPES = [
  { value: 'todos', label: 'Todos los tipos', icon: Search },
  { value: 'casa', label: 'Casa', icon: HomeIcon },
  { value: 'departamento', label: 'Departamento', icon: Building2 },
  { value: 'terreno', label: 'Terreno', icon: MapIcon },
  { value: 'local', label: 'Local Comercial', icon: Store },
  { value: 'oficina', label: 'Oficina', icon: Building }
];

const PROJECT_TYPES = [
  { value: 'todos', label: 'Todos los tipos', icon: Search },
  { value: 'residencial', label: 'Residencial', icon: Building2 },
  { value: 'comercial', label: 'Comercial', icon: Store },
  { value: 'mixto', label: 'Mixto', icon: Building }
];

const PROJECT_STAGES = [
  { value: 'todos', label: 'Todas las etapas' },
  { value: 'planos', label: 'En planos' },
  { value: 'lanzamiento', label: 'Lanzamiento' },
  { value: 'construccion', label: 'En construcción' },
  { value: 'terminado', label: 'Terminado' }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  },
  hover: {
    y: -8,
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  }
};

// SearchBar Components
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <motion.button
    onClick={onClick}
    className={`group relative overflow-hidden px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl
              transition-all duration-300 ${
      active 
        ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md' 
        : 'text-white/80 dark:text-gray-300 hover:text-white dark:hover:text-white hover:bg-white/10 dark:hover:bg-white/10'
    }`}
    whileHover={{ scale: 1.01, y: -0.5 }}
    whileTap={{ scale: 0.99 }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                    translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <span className="relative z-10 flex items-center gap-1 sm:gap-1.5">
      {Icon && <Icon className="hidden sm:block w-3.5 h-3.5" />}
      {children}
    </span>
  </motion.button>
);

const PropertyTypeButton = ({ icon: Icon, label, selected, onClick }) => (
  <motion.button
    onClick={onClick}
    className={`group relative overflow-hidden flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300
              ${selected 
      ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white shadow-md' 
      : 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'}`}
    whileHover={{ scale: 1.01, y: -1 }}
    whileTap={{ scale: 0.99 }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                    translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
    <Icon className={`hidden sm:block w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 relative z-10
                   ${selected ? 'text-white' : 'text-blue-500 dark:text-blue-400 group-hover:scale-105'}`} />
    <span className="font-medium relative z-10 text-sm">{label}</span>
    {selected && <Sparkles className="hidden sm:block w-3 h-3 text-white/70 ml-auto relative z-10" />}
  </motion.button>
);

const FilterInputSearch = ({ label, value, onChange, placeholder, type = "text", min, icon: Icon }) => (
  <div className="space-y-2">
    <label className="block text-xs font-medium text-white/90 dark:text-gray-300 flex items-center gap-1.5">
      {Icon && <Icon className="hidden sm:block w-3.5 h-3.5" />}
      {label}
    </label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        min={min}
        className="w-full px-3 sm:px-3.5 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 placeholder-white/50 dark:placeholder-gray-400 text-sm
                 border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30 
                 focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium"
      />
      <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 
                      opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  </div>
);

const FilterButton = ({ onClick, active, children }) => (
  <motion.button
    onClick={onClick}
    className={`group flex items-center gap-2 px-6 py-3 rounded-xl 
              transition-all duration-300
              ${active 
      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg dark:shadow-xl' 
      : 'bg-white dark:bg-gray-800 hover:bg-indigo-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600'
    }`}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <div className={`transition-transform duration-300 group-hover:rotate-3 
                  ${active ? 'text-white' : 'text-indigo-500 dark:text-indigo-400'}`}>
      {children}
    </div>
  </motion.button>
);

const SortButton = ({ value, options, onChange }) => (
  <div className="relative group">
    <select
      value={value}
      onChange={onChange}
      className="appearance-none w-full bg-white dark:bg-gray-800 px-6 py-3 pr-10 rounded-xl
                border-2 border-transparent hover:border-indigo-100 dark:hover:border-indigo-800
                focus:border-indigo-200 dark:focus:border-indigo-600 focus:outline-none
                transition-all duration-300 cursor-pointer text-gray-700 dark:text-gray-200
                shadow-sm dark:shadow-lg
                [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 
                         text-gray-400 dark:text-gray-500 w-5 h-5 pointer-events-none
                         transition-transform duration-300
                         group-hover:text-indigo-500 dark:group-hover:text-indigo-400" />
  </div>
);

const FilterInput = ({ label, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
    {children}
  </div>
);

const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-[calc(100vh-6rem)] gap-4">
    <motion.div
      className="relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="w-16 h-16 border-4 border-indigo-200 dark:border-indigo-800 rounded-full animate-spin">
        <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-600 dark:border-indigo-400 rounded-full animate-pulse border-t-transparent"></div>
      </div>
    </motion.div>
    <p className="text-indigo-700 dark:text-indigo-300 font-medium animate-pulse">Buscando propiedades...</p>
  </div>
);

const NoResults = ({ onReset, searchType = 'propiedades' }) => (
  <motion.div 
    className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-black/50 border border-gray-100 dark:border-gray-700"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
  >
    <div className="bg-indigo-50 dark:bg-indigo-900/30 w-20 h-20 rounded-full flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
    </div>
    <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
      No se encontraron {searchType}
    </h2>
    <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md text-center">
      Intenta modificar tus criterios de búsqueda para encontrar más opciones
    </p>
    <motion.button 
      onClick={onReset}
      className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl
                shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <span className="flex items-center gap-2">
        <XCircle className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
        Limpiar Filtros
      </span>
    </motion.button>
  </motion.div>
);

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <motion.div 
    className="flex justify-center items-center space-x-2 mt-12"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <motion.button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage <= 1}
      className={`flex items-center justify-center w-10 h-10 rounded-lg
                ${currentPage <= 1 
        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
        : 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'}`}
      whileHover={currentPage > 1 ? { scale: 1.1 } : {}}
      whileTap={currentPage > 1 ? { scale: 0.95 } : {}}
    >
      <ChevronLeft className="w-5 h-5" />
    </motion.button>
    
    {[...Array(totalPages)].map((_, index) => {
      const pageNumber = index + 1;
      const isActive = pageNumber === currentPage;
      
      return (
        <motion.button
          key={pageNumber}
          onClick={() => onPageChange(pageNumber)}
          className={`flex items-center justify-center w-10 h-10 rounded-lg
                    ${isActive 
            ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium shadow-lg dark:shadow-xl' 
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'}`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {pageNumber}
        </motion.button>
      );
    })}
    
    <motion.button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage >= totalPages}
      className={`flex items-center justify-center w-10 h-10 rounded-lg
                ${currentPage >= totalPages 
        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' 
        : 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'}`}
      whileHover={currentPage < totalPages ? { scale: 1.1 } : {}}
      whileTap={currentPage < totalPages ? { scale: 0.95 } : {}}
    >
      <ChevronRight className="w-5 h-5" />
    </motion.button>
  </motion.div>
);

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { propiedades, proyectos, loading, error, searchPropiedades, searchAlquileres, searchProyectos } = usePropiedades();
  
  // Estados del SearchBar integrado
  const [activeTab, setActiveTab] = useState(() => {
    const operacion = searchParams.get('operacion');
    if (operacion === 'alquilar') return 'alquilar';
    if (operacion === 'proyectos') return 'proyectos';
    return 'comprar';
  });
  
  const [tipo, setTipo] = useState(() => {
    const tipoParam = searchParams.get('tipo');
    return tipoParam || 'todos';
  });
  
  const [estadoProyecto, setEstadoProyecto] = useState(() => {
    const estadoParam = searchParams.get('estado_proyecto');
    return estadoParam || 'todos';
  });
  
  const [searchTitle, setSearchTitle] = useState(() => searchParams.get('tituloSearch') || '');
  const [precioMin, setPrecioMin] = useState(() => searchParams.get('precioMin') || '');
  const [precioMax, setPrecioMax] = useState(() => searchParams.get('precioMax') || '');
  const [ubicacion, setUbicacion] = useState(() => searchParams.get('ubicacion') || '');
  const [dormitorios, setDormitorios] = useState(() => searchParams.get('dormitorios') || '');
  
  const [showSearchFilters, setShowSearchFilters] = useState(false);
  const [showPropertyTypes, setShowPropertyTypes] = useState(false);
  const [showProjectStages, setShowProjectStages] = useState(false);
  
  // Estado para almacenar datos de ubicación seleccionada del SearchBar
  const [ubicacionData, setUbicacionData] = useState(null);
  
  // Referencias para controlar clicks fuera del dropdown del SearchBar
  const propertyTypeRef = useRef(null);
  const projectStageRef = useRef(null);
  
  // Estados originales del SearchResults
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState('recent');
  const [filters, setFilters] = useState({
    bedrooms: '',
    bathrooms: '',
    area: { min: '', max: '' },
    ubicacion: searchParams.get('ubicacion') || '',
    ubicacionData: null,
    estadoProyecto: searchParams.get('estado_proyecto') || '',
    dormitorios: searchParams.get('dormitorios') || ''
  });
  
  // Determinar el tipo de búsqueda (propiedades, alquileres o proyectos)
  const [searchType, setSearchType] = useState(() => {
    const operacion = searchParams.get('operacion');
    if (operacion === 'proyectos') return 'proyectos';
    if (operacion === 'alquilar') return 'alquileres';
    return 'propiedades';
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = searchType === 'proyectos' ? 6 : 12; // 6 para proyectos, 12 para propiedades

  // Referencias para manejo de dropdowns de filtros
  const locationInputRef = useRef(null);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Create a unique key based on search params
  const searchParamsKey = useMemo(() => {
    const params = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return JSON.stringify(params);
  }, [searchParams]);

  // Cerrar sugerencias al hacer clic fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      // SearchBar dropdowns
      if (propertyTypeRef.current && !propertyTypeRef.current.contains(event.target)) {
        setShowPropertyTypes(false);
      }
      if (projectStageRef.current && !projectStageRef.current.contains(event.target)) {
        setShowProjectStages(false);
      }
      // Filter dropdowns
      if (locationInputRef.current && !locationInputRef.current.contains(event.target)) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Inicializar filtros desde los parámetros de búsqueda
  useEffect(() => {
    // Actualizar el tipo de búsqueda
    const operacion = searchParams.get('operacion');
    if (operacion === 'proyectos') setSearchType('proyectos');
    else if (operacion === 'alquilar') setSearchType('alquileres');
    else setSearchType('propiedades');
    
    // Actualizar estados del SearchBar
    setActiveTab(operacion === 'proyectos' ? 'proyectos' : operacion === 'alquilar' ? 'alquilar' : 'comprar');
    setTipo(searchParams.get('tipo') || searchParams.get('tipo_proyecto') || 'todos');
    setSearchTitle(searchParams.get('tituloSearch') || '');
    setPrecioMin(searchParams.get('precioMin') || '');
    setPrecioMax(searchParams.get('precioMax') || '');
    setUbicacion(searchParams.get('ubicacion') || '');
    
    // Actualizar filtros comunes
    const ubicacionParam = searchParams.get('ubicacion');
    if (ubicacionParam) {
      setFilters(prev => ({
        ...prev, 
        ubicacion: ubicacionParam
      }));
    }
    
    // Actualizar filtros específicos de proyectos
    if (operacion === 'proyectos') {
      const estadoProyectoParam = searchParams.get('estado_proyecto');
      const dormitoriosParam = searchParams.get('dormitorios');
      
      setEstadoProyecto(estadoProyectoParam || 'todos');
      setDormitorios(dormitoriosParam || '');
      
      setFilters(prev => ({
        ...prev,
        estadoProyecto: estadoProyectoParam || '',
        dormitorios: dormitoriosParam || ''
      }));
    }
    
    // Reset to first page when search params change
    setCurrentPage(1);
  }, [searchParams]);

  // Opciones de ordenamiento según el tipo de búsqueda
  const sortOptions = useMemo(() => {
    const commonOptions = [
      { value: 'recent', label: 'Más recientes' },
      { value: 'priceAsc', label: 'Precio: Menor a Mayor' },
      { value: 'priceDesc', label: 'Precio: Mayor a Menor' }
    ];
    
    if (searchType === 'proyectos') {
      return [
        ...commonOptions,
        { value: 'entregaAsc', label: 'Entrega: Más próxima' },
        { value: 'entregaDesc', label: 'Entrega: Más lejana' }
      ];
    } else {
      return [
        ...commonOptions,
        { value: 'bedroomsDesc', label: 'Más habitaciones' },
        { value: 'bathroomsDesc', label: 'Más baños' }
      ];
    }
  }, [searchType]);

  // SearchBar handlers
  const handleSearch = async (params) => {
    const searchParams = {
      operacion: params.operacion || activeTab,
      tipo: params.tipo || (tipo !== 'todos' ? tipo : ''),
      tituloSearch: params.tituloSearch || searchTitle.trim(),
      precioMin: params.precioMin || precioMin,
      precioMax: params.precioMax || precioMax,
      ubicacion: params.ubicacion || ubicacion
    };

    // Añadir parámetros específicos para proyectos
    if (activeTab === 'proyectos') {
      searchParams.tipo_proyecto = searchParams.tipo;
      searchParams.estado_proyecto = params.estado_proyecto || (estadoProyecto !== 'todos' ? estadoProyecto : '');
      searchParams.dormitorios = params.dormitorios || dormitorios;
      delete searchParams.tipo;
    }

    // Añadir datos específicos de ubicación si están disponibles
    if (ubicacionData) {
      if (ubicacionData.type === LocationType.CITY) {
        searchParams.ciudad = ubicacionData.value;
        searchParams.pais = ubicacionData.extraData?.countryCode;
        
        if (ubicacionData.extraData?.latitude && ubicacionData.extraData?.longitude) {
          searchParams.lat = ubicacionData.extraData.latitude;
          searchParams.lng = ubicacionData.extraData.longitude;
        }
      }
    }

    try {
      if (searchParams.operacion === 'alquilar') {
        await searchAlquileres(searchParams);
      } else if (searchParams.operacion === 'proyectos') {
        await searchProyectos(searchParams);
      } else {
        await searchPropiedades(searchParams);
      }
    } catch (error) {
      console.error("Error al realizar la búsqueda:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const searchParams = {
      operacion: activeTab,
      tituloSearch: searchTitle.trim(),
      precioMin: precioMin || '',
      precioMax: precioMax || '',
      ubicacion: ubicacion || ''
    };

    if (activeTab === 'proyectos') {
      searchParams.tipo_proyecto = tipo !== 'todos' ? tipo : '';
      searchParams.estado_proyecto = estadoProyecto !== 'todos' ? estadoProyecto : '';
      searchParams.dormitorios = dormitorios;
    } else {
      searchParams.tipo = tipo !== 'todos' ? tipo : '';
    }

    if (ubicacionData) {
      if (ubicacionData.type === LocationType.CITY) {
        searchParams.ciudad = ubicacionData.value;
        searchParams.pais = ubicacionData.extraData?.countryCode;
      }
    }

    handleSearch(searchParams);

    const queryParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });
    
    navigate(`/buscar?${queryParams.toString()}`);
  };

  const handleReset = () => {
    setSearchTitle('');
    setPrecioMin('');
    setPrecioMax('');
    setUbicacion('');
    setUbicacionData(null);
    setTipo('todos');
    setEstadoProyecto('todos');
    setDormitorios('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTipo('todos');
    setEstadoProyecto('todos');
  };

  const handleTipoChange = (newTipo) => {
    setTipo(newTipo);
    setShowPropertyTypes(false);
  };

  const handleEstadoProyectoChange = (newEstado) => {
    setEstadoProyecto(newEstado);
    setShowProjectStages(false);
  };

  // Function to perform search based on search params
  const performSearch = useCallback(() => {
    const operacion = searchParams.get('operacion');
    
    // Verificar si estamos buscando proyectos
    if (operacion === 'proyectos') {
      const filters = {
        tipo_proyecto: searchParams.get('tipo_proyecto') || '',
        estado_proyecto: searchParams.get('estado_proyecto') || '',
        dormitorios: searchParams.get('dormitorios') || '',
        tituloSearch: searchParams.get('tituloSearch') || '',
        precioMin: searchParams.get('precioMin') || '',
        precioMax: searchParams.get('precioMax') || '',
        ubicacion: searchParams.get('ubicacion') || ''
      };
      
      console.log('Searching projects with filters:', filters);
      
      if (Object.values(filters).some(value => value !== '')) {
        searchProyectos(filters);
      }
    } else {
      // Búsqueda normal de propiedades o alquileres
      const filters = {
        operacion: operacion === 'alquilar' ? 'alquiler' : operacion,
        tipo: searchParams.get('tipo') || '',
        tituloSearch: searchParams.get('tituloSearch') || '',
        precioMin: searchParams.get('precioMin') || '',
        precioMax: searchParams.get('precioMax') || '',
        ubicacion: searchParams.get('ubicacion') || ''
      };

      console.log('Searching properties with filters:', filters);

      if (Object.values(filters).some(value => value !== '')) {
        if (operacion === 'alquilar') {
          searchAlquileres(filters);
        } else {
          searchPropiedades(filters);
        }
      }
    }
  }, [searchParams, searchPropiedades, searchAlquileres, searchProyectos]);

  // Execute search when parameters change
  useEffect(() => {
    performSearch();
  }, [searchParamsKey, performSearch]);

  // Obtener los datos correctos según el tipo de búsqueda
  const getDataToDisplay = useCallback(() => {
    return searchType === 'proyectos' ? proyectos : propiedades;
  }, [searchType, propiedades, proyectos]);

  // Function to sort properties
  const sortData = useCallback((data) => {
    // Si no hay datos, devolver un array vacío
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    // Para propiedades y alquileres
    if (searchType !== 'proyectos') {
      const getPrecio = (property) => {
        const isAlquiler = property.operacion === 'alquiler';
        return parseFloat(isAlquiler ? property.precio_mensual : property.precio_soles) || 0;
      };

      switch(sortOption) {
        case 'priceAsc':
          return [...data].sort((a, b) => getPrecio(a) - getPrecio(b));
        case 'priceDesc':
          return [...data].sort((a, b) => getPrecio(b) - getPrecio(a));
        case 'bedroomsDesc':
          return [...data].sort((a, b) => parseInt(b.habitaciones || 0) - parseInt(a.habitaciones || 0));
        case 'bathroomsDesc':
          return [...data].sort((a, b) => parseInt(b.banos || 0) - parseInt(a.banos || 0));
        case 'recent':
        default:
          return [...data].sort((a, b) => 
            new Date(b.fecha_publicacion || '2000-01-01') - new Date(a.fecha_publicacion || '2000-01-01')
          );
      }
    } 
    // Para proyectos
    else {
      const getPrecio = (project) => {
        // Obtener el precio mínimo del proyecto
        if (project.precio_desde) return parseFloat(project.precio_desde);
        
        // Si no está directamente disponible, calcularlo a partir de los precios disponibles
        const precios = [];
        if (project.tiene_1dorm && project.precio_1dorm_soles) precios.push(parseFloat(project.precio_1dorm_soles));
        if (project.tiene_2dorm && project.precio_2dorm_soles) precios.push(parseFloat(project.precio_2dorm_soles));
        if (project.tiene_3dorm && project.precio_3dorm_soles) precios.push(parseFloat(project.precio_3dorm_soles));
        if (project.tiene_4dorm && project.precio_4dorm_soles) precios.push(parseFloat(project.precio_4dorm_soles));
        
        return precios.length > 0 ? Math.min(...precios) : 0;
      };
      
      const getFechaEntrega = (project) => {
        const mes = project.mes_entrega || '';
        const anio = project.anio_entrega || '';
        if (!mes || !anio) return new Date('2099-12-31');
        const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
        const mesIndex = meses.findIndex(m => m.toLowerCase() === mes.toLowerCase());
        return new Date(parseInt(anio), mesIndex !== -1 ? mesIndex : 0, 1);
      };

      switch(sortOption) {
        case 'priceAsc':
          return [...data].sort((a, b) => getPrecio(a) - getPrecio(b));
        case 'priceDesc':
          return [...data].sort((a, b) => getPrecio(b) - getPrecio(a));
        case 'entregaAsc':
          return [...data].sort((a, b) => getFechaEntrega(a) - getFechaEntrega(b));
        case 'entregaDesc':
          return [...data].sort((a, b) => getFechaEntrega(b) - getFechaEntrega(a));
        case 'recent':
        default:
          return [...data].sort((a, b) => 
            new Date(b.fecha_publicacion || '2000-01-01') - new Date(a.fecha_publicacion || '2000-01-01')
          );
      }
    }
  }, [sortOption, searchType]);

  // Memoize sorted data
  const sortedData = useMemo(() => 
    sortData(getDataToDisplay()),
    [getDataToDisplay, sortData]
  );
  
  // Calculate paginated data and total pages
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, ITEMS_PER_PAGE]);
  
  const totalPages = useMemo(() => 
    Math.max(1, Math.ceil(sortedData.length / ITEMS_PER_PAGE)),
    [sortedData, ITEMS_PER_PAGE]
  );

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      ubicacion: value,
      ubicacionData: null // Reset location data when typing
    }));
    setShowLocationSuggestions(true);
  };

  const handleLocationSelect = (locationData) => {
    setFilters(prev => ({
      ...prev,
      ubicacion: locationData.display,
      ubicacionData: locationData
    }));
    setShowLocationSuggestions(false);
  };
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of results when page changes
    window.scrollTo({
      top: document.querySelector('.results-container')?.offsetTop - 100 || 0,
      behavior: 'smooth'
    });
  };

  const applyAdditionalFilters = () => {
    // Determinar operación actual
    const operacion = searchParams.get('operacion');
    
    if (operacion === 'proyectos') {
      // Crear filtros para proyectos
      const projectFilters = {
        tipo_proyecto: searchParams.get('tipo_proyecto') || '',
        estado_proyecto: filters.estadoProyecto || '',
        dormitorios: filters.dormitorios || '',
        tituloSearch: searchParams.get('tituloSearch') || '',
        precioMin: searchParams.get('precioMin') || '',
        precioMax: searchParams.get('precioMax') || '',
        ubicacion: filters.ubicacion || ''
      };
      
      // Si tenemos datos específicos de ubicación
      if (filters.ubicacionData) {
        projectFilters.ubicacionTipo = filters.ubicacionData.type;
        projectFilters.ubicacionValor = filters.ubicacionData.value;
      }
      
      searchProyectos(projectFilters);
    } else {
      // Búsqueda normal de propiedades o alquileres
      const propertyFilters = {
        operacion: operacion === 'alquilar' ? 'alquiler' : operacion,
        tipo: searchParams.get('tipo') || '',
        tituloSearch: searchParams.get('tituloSearch') || '',
        precioMin: searchParams.get('precioMin') || '',
        precioMax: searchParams.get('precioMax') || '',
        ubicacion: filters.ubicacion || '',
        // Additional filters from the UI
        habitaciones: filters.bedrooms || '',
        banos: filters.bathrooms || '',
        areaMin: filters.area.min || '',
        areaMax: filters.area.max || ''
      };

      // Si tenemos datos específicos de ubicación
      if (filters.ubicacionData) {
        propertyFilters.ubicacionTipo = filters.ubicacionData.type;
        propertyFilters.ubicacionValor = filters.ubicacionData.value;
      }

      if (operacion === 'alquilar') {
        searchAlquileres(propertyFilters);
      } else {
        searchPropiedades(propertyFilters);
      }
    }
    
    setCurrentPage(1); // Reset to first page when filters are applied
  };

  // Resetear todos los filtros según el tipo de búsqueda
  const handleResetFilters = () => {
    const baseReset = {
      bedrooms: '',
      bathrooms: '',
      area: { min: '', max: '' },
      ubicacion: '',
      ubicacionData: null
    };
    
    // Si estamos en proyectos, resetear también los filtros específicos
    if (searchType === 'proyectos') {
      setFilters({
        ...baseReset,
        estadoProyecto: '',
        dormitorios: ''
      });
    } else {
      setFilters(baseReset);
    }
    
    // Reset SearchBar states
    setSearchTitle('');
    setPrecioMin('');
    setPrecioMax('');
    setUbicacion('');
    setUbicacionData(null);
    setTipo('todos');
    setEstadoProyecto('todos');
    setDormitorios('');
    
    setCurrentPage(1);
    
    // Realizar búsqueda sin filtros según el tipo
    if (searchType === 'proyectos') {
      searchProyectos({});
    } else if (searchType === 'alquileres') {
      searchAlquileres({});
    } else {
      searchPropiedades({});
    }
  };

  // Determinar el título de la página según el tipo de búsqueda
  const getPageTitle = () => {
    switch(searchType) {
      case 'proyectos':
        return "Proyectos Inmobiliarios";
      case 'alquileres':
        return "Propiedades en Alquiler";
      default:
        return "Propiedades en Venta";
    }
  };
  
  // Determinar el mensaje de "No se encontraron..." según el tipo de búsqueda
  const getNoResultsType = () => {
    switch(searchType) {
      case 'proyectos':
        return "proyectos";
      case 'alquileres':
        return "propiedades en alquiler";
      default:
        return "propiedades en venta";
    }
  };

  const currentPropertyTypes = activeTab === 'proyectos' ? PROJECT_TYPES : PROPERTY_TYPES;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-x-hidden">
      {/* Hero Banner Section styled like Home */}
      <section className="relative" style={{ zIndex: 10 }}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-900">
          {/* Animated Orbs - Con "overflow-hidden" aplicado y posición contenida */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl"
                style={{
                  width: `${Math.random() * 300 + 100}px`,
                  height: `${Math.random() * 300 + 100}px`,
                  // Limitar la posición a un rango seguro (0-90% en vez de 0-100%)
                  top: `${Math.random() * 90}%`,
                  left: `${Math.random() * 90}%`,
                }}
                animate={{
                  // Limitar la animación para que no exceda los límites
                  x: [0, Math.random() * 50 - 25, 0],
                  y: [0, Math.random() * 50 - 25, 0],
                  scale: [1, Math.random() * 0.3 + 1, 1],
                }}
                transition={{
                  duration: Math.random() * 10 + 15,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
          
          {/* Mesh Grid Overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        {/* Content Section */}
        <div className="relative py-12" style={{ zIndex: 20 }}>
          <motion.div 
            className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Encuentra{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-200">
                {getPageTitle()}
              </span>
            </h1>
            <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
              Filtra, ordena y descubre opciones que se ajustan a tus necesidades
            </p>
          </motion.div>
          
          {/* SearchBar integrado */}
          <div className="relative" style={{ zIndex: 999 }}>
            <motion.div 
              className="w-full max-w-4xl mx-auto px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Enhanced Main Search Container */}
              <motion.div 
                className="bg-white/10 dark:bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl dark:shadow-black/50 border border-white/20 dark:border-white/10 relative"
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
              >
                <form onSubmit={handleSubmit}>
                  {/* Enhanced Header with Tabs */}
                  <div className="relative overflow-hidden rounded-t-2xl">
                    {/* Decorative background pattern */}
                    <div className="absolute inset-0">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10" />
                      <div className="absolute top-0 left-1/4 w-24 h-24 bg-blue-400/10 rounded-full blur-2xl" />
                      <div className="absolute top-0 right-1/4 w-24 h-24 bg-purple-400/10 rounded-full blur-2xl" />
                    </div>
                    
                    <div className="relative flex justify-center p-3 sm:p-5 pb-2 sm:pb-3">
                      <div className="flex gap-1 sm:gap-1.5 p-1 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20 dark:border-white/10">
                        <TabButton
                          active={activeTab === 'comprar'}
                          onClick={() => handleTabChange('comprar')}
                          icon={HomeIcon}
                        >
                          Comprar
                        </TabButton>
                        <TabButton
                          active={activeTab === 'alquilar'}
                          onClick={() => handleTabChange('alquilar')}
                          icon={Building2}
                        >
                          Alquilar
                        </TabButton>
                        <TabButton
                          active={activeTab === 'proyectos'}
                          onClick={() => handleTabChange('proyectos')}
                          icon={TrendingUp}
                        >
                          Proyectos
                        </TabButton>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Search Section */}
                  <div className="p-3 sm:p-5 pt-1 sm:pt-2 relative overflow-visible">
                    {/* Enhanced Search Input */}
                    <div className="mb-3 sm:mb-4">
                      <div className="relative group">
                        <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-white/60 dark:text-gray-400 w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 group-focus-within:text-blue-400 group-focus-within:scale-105" />
                        <input
                          type="text"
                          placeholder={activeTab === 'proyectos' ? "Buscar proyectos..." : "Buscar propiedades..."}
                          value={searchTitle}
                          onChange={(e) => setSearchTitle(e.target.value)}
                          maxLength={50}
                          className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-3.5 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 placeholder-white/50 dark:placeholder-gray-400 text-sm sm:text-base
                                  border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30
                                  focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium"
                        />
                        <div className="absolute inset-0 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 
                                        opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        <Sparkles className="hidden sm:block absolute right-4 top-1/2 transform -translate-y-1/2 text-white/30 dark:text-gray-500 w-4 h-4 opacity-0 group-focus-within:opacity-100 transition-all duration-300" />
                      </div>
                    </div>

                    {/* Enhanced Bottom Row */}
                    <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 relative overflow-visible"
                         style={{ zIndex: 1000 }}>
                      {/* Enhanced Property Type Selection */}
                      <div className="flex-1 relative overflow-visible" ref={propertyTypeRef} style={{ zIndex: 1000 }}>
                        <motion.div
                          className="group flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm 
                                    border border-white/20 dark:border-white/10 cursor-pointer text-white dark:text-gray-200 transition-all duration-300
                                    hover:bg-white/15 dark:hover:bg-white/10 hover:border-white/30 dark:hover:border-white/20"
                          onClick={() => setShowPropertyTypes(!showPropertyTypes)}
                          whileHover={{ scale: 1.005, y: -0.5 }}
                          whileTap={{ scale: 0.995 }}
                        >
                          {(() => {
                            const Icon = currentPropertyTypes.find(t => t.value === tipo)?.icon;
                            return Icon ? <Icon className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-white/80 dark:text-gray-300 transition-transform duration-300 group-hover:scale-105" /> : null;
                          })()}
                          <span className="font-medium text-left text-sm sm:text-base flex-1">{currentPropertyTypes.find(t => t.value === tipo)?.label}</span>
                          <ChevronDown className={`w-4 h-4 transition-all duration-300 ${
                            showPropertyTypes ? 'rotate-180 text-blue-300 dark:text-blue-400' : 'group-hover:text-blue-300 dark:group-hover:text-blue-400'
                          }`} />
                        </motion.div>

                        <AnimatePresence>
                          {showPropertyTypes && (
                            <motion.div 
                              className="absolute left-0 right-0 top-full mt-2 p-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl dark:shadow-black/50
                                        grid grid-cols-1 sm:grid-cols-2 gap-2 border border-white/20 dark:border-gray-600
                                        max-h-60 overflow-y-auto"
                              style={{ zIndex: 10000 }}
                              initial={{ opacity: 0, y: -8, scale: 0.98 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: -8, scale: 0.98 }}
                              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                            >
                              {currentPropertyTypes.map((type) => (
                                <PropertyTypeButton
                                  key={type.value}
                                  icon={type.icon}
                                  label={type.label}
                                  selected={tipo === type.value}
                                  onClick={() => handleTipoChange(type.value)}
                                />
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Enhanced Project Stage Selection */}
                      {activeTab === 'proyectos' && (
                        <div className="flex-1 relative overflow-visible" ref={projectStageRef} style={{ zIndex: 1000 }}>
                          <motion.div
                            className="group flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm 
                                      border border-white/20 dark:border-white/10 cursor-pointer text-white dark:text-gray-200 transition-all duration-300
                                      hover:bg-white/15 dark:hover:bg-white/10 hover:border-white/30 dark:hover:border-white/20"
                            onClick={() => setShowProjectStages(!showProjectStages)}
                            whileHover={{ scale: 1.005, y: -0.5 }}
                            whileTap={{ scale: 0.995 }}
                          >
                            <Castle className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-white/80 dark:text-gray-300 transition-transform duration-300 group-hover:scale-105" />
                            <span className="font-medium text-left text-sm sm:text-base flex-1">{PROJECT_STAGES.find(s => s.value === estadoProyecto)?.label}</span>
                            <ChevronDown className={`w-4 h-4 transition-all duration-300 ${
                              showProjectStages ? 'rotate-180 text-blue-300 dark:text-blue-400' : 'group-hover:text-blue-300 dark:group-hover:text-blue-400'
                            }`} />
                          </motion.div>

                          <AnimatePresence>
                            {showProjectStages && (
                              <motion.div 
                                className="absolute left-0 right-0 top-full mt-2 p-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-2xl dark:shadow-black/50
                                          space-y-1.5 border border-white/20 dark:border-gray-600
                                          max-h-60 overflow-y-auto"
                                style={{ zIndex: 10000 }}
                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                              >
                                {PROJECT_STAGES.map((stage) => (
                                  <motion.button
                                    key={stage.value}
                                    onClick={() => handleEstadoProyectoChange(stage.value)}
                                    className={`group relative overflow-hidden w-full flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-lg sm:rounded-xl transition-all duration-300
                                              ${estadoProyecto === stage.value 
                                      ? 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 text-white shadow-md' 
                                      : 'bg-white/60 dark:bg-gray-700/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700'}`}
                                    whileHover={{ scale: 1.01, y: -0.5 }}
                                    whileTap={{ scale: 0.99 }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                                    translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                                    <span className="font-medium relative z-10 text-sm">{stage.label}</span>
                                    {estadoProyecto === stage.value && <Sparkles className="hidden sm:block w-3 h-3 text-white/70 ml-auto relative z-10" />}
                                  </motion.button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Enhanced Action Buttons */}
                      <div className="flex gap-2 sm:gap-2.5">
                        <motion.button
                          type="button"
                          onClick={() => setShowSearchFilters(!showSearchFilters)}
                          className={`group relative overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-medium text-sm sm:text-base
                                   transition-all duration-300 flex-1 lg:flex-none ${
                            showSearchFilters 
                              ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md' 
                              : 'bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 border border-white/20 dark:border-white/10 hover:bg-white/15 dark:hover:bg-white/10'
                          }`}
                          whileHover={{ scale: 1.01, y: -0.5 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                          translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                          <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 group-hover:rotate-12 relative z-10" />
                          <span className="relative z-10">Filtros</span>
                        </motion.button>

                        <motion.button
                          type="submit"
                          className="group relative overflow-hidden flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 
                                   text-white rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/25 dark:shadow-blue-500/30 font-medium text-sm sm:text-base flex-1 lg:flex-none"
                          whileHover={{ 
                            scale: 1.01, 
                            y: -1,
                            boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)" 
                          }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 
                                          translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                          <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-all duration-300 group-hover:scale-105 relative z-10" />
                          <span className="relative z-10">Buscar</span>
                        </motion.button>
                      </div>
                    </div>

                    {/* Enhanced Filters Section */}
                    <AnimatePresence>
                      {showSearchFilters && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                          className="mt-4"
                        >
                          <div className="p-3 sm:p-4 bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20 dark:border-white/10">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                              {/* Enhanced Location Search */}
                              <div className="relative" style={{ zIndex: 1000 }}>
                                <label className="block text-xs font-medium text-white/90 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                                  <MapPin className="hidden sm:block w-3.5 h-3.5" />
                                  Ubicación
                                </label>
                                <div className="relative">
                                  <EnhancedLocationSearch
                                    value={ubicacion}
                                    onChange={(value) => setUbicacion(value)}
                                    onSelect={(locationData) => setUbicacionData(locationData)}
                                  />
                                </div>
                              </div>

                              {/* Enhanced Price Inputs */}
                              <FilterInputSearch
                                label={`Precio ${activeTab === 'alquilar' ? 'mensual' : activeTab === 'proyectos' ? 'desde' : ''} mínimo (S/)`}
                                value={precioMin}
                                onChange={(e) => setPrecioMin(e.target.value)}
                                placeholder="Mínimo"
                                type="number"
                                min="0"
                                icon={TrendingUp}
                              />

                              <FilterInputSearch
                                label={`Precio ${activeTab === 'alquilar' ? 'mensual' : activeTab === 'proyectos' ? 'desde' : ''} máximo (S/)`}
                                value={precioMax}
                                onChange={(e) => setPrecioMax(e.target.value)}
                                placeholder="Máximo"
                                type="number"
                                min="0"
                                icon={TrendingUp}
                              />

                              {/* Enhanced Dormitorios for projects */}
                              {activeTab === 'proyectos' && (
                                <div className="space-y-2">
                                  <label className="block text-xs font-medium text-white/90 dark:text-gray-300 flex items-center gap-1.5">
                                    <HomeIcon className="hidden sm:block w-3.5 h-3.5" />
                                    Dormitorios
                                  </label>
                                  <div className="relative">
                                    <select
                                      value={dormitorios}
                                      onChange={(e) => setDormitorios(e.target.value)}
                                      className="w-full px-3 sm:px-3.5 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 appearance-none text-sm
                                               border border-white/20 dark:border-white/10 focus:outline-none focus:ring-3 focus:ring-blue-500/30 
                                               focus:border-blue-400/50 dark:focus:border-blue-400/50 transition-all duration-300 font-medium
                                               [&>option]:bg-gray-800 [&>option]:text-gray-100"
                                    >
                                      <option value="" className="bg-gray-800 text-gray-100">Todos</option>
                                      <option value="1" className="bg-gray-800 text-gray-100">1 Dormitorio</option>
                                      <option value="2" className="bg-gray-800 text-gray-100">2 Dormitorios</option>
                                      <option value="3" className="bg-gray-800 text-gray-100">3 Dormitorios</option>
                                      <option value="4" className="bg-gray-800 text-gray-100">4+ Dormitorios</option>
                                    </select>
                                    <ChevronDown className="absolute right-3 sm:right-3.5 top-1/2 transform -translate-y-1/2 text-white/60 dark:text-gray-400 w-4 h-4 pointer-events-none" />
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Enhanced Filter Actions */}
                            <div className="flex justify-end gap-2 sm:gap-3 mt-3 sm:mt-4 pt-3 border-t border-white/20 dark:border-white/10">
                              <motion.button
                                type="button"
                                onClick={handleReset}
                                className="group flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white/10 dark:bg-white/5 backdrop-blur-sm text-white dark:text-gray-200 rounded-lg sm:rounded-xl
                                         border border-white/20 dark:border-white/10 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300 font-medium text-xs sm:text-sm"
                                whileHover={{ scale: 1.01, y: -0.5 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                <X className="w-3 sm:w-3.5 h-3 sm:h-3.5 transition-transform duration-300 group-hover:rotate-90" />
                                <span>Limpiar</span>
                              </motion.button>
                              <motion.button
                                type="submit"
                                className="group flex items-center gap-1.5 px-4 sm:px-5 py-2 bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 rounded-lg sm:rounded-xl
                                         shadow-md font-medium hover:shadow-lg transition-all duration-300 text-xs sm:text-sm"
                                whileHover={{ scale: 1.01, y: -0.5 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                <Check className="w-3 sm:w-3.5 h-3 sm:h-3.5 transition-transform duration-300 group-hover:scale-105" />
                                <span>Aplicar</span>
                                <ArrowRight className="w-3 sm:w-3.5 h-3 sm:h-3.5 ml-0.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contenedor principal */}
      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8 -mt-6 relative" 
        style={{ zIndex: 1, overflow: 'visible' }}
      >
        {/* Results Header */}
        <motion.div 
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center 
                   space-y-4 sm:space-y-0 mb-8 backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-xl shadow-lg dark:shadow-black/50 p-4 border border-white/20 dark:border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
              {searchType === 'proyectos' ? (
                <Castle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              ) : (
                <Search className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              {sortedData.length}{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-blue-600">
                {searchType === 'proyectos' ? 'Proyectos Encontrados' : 'Propiedades Encontradas'}
              </span>
            </h2>
          </div>

          <div className="flex items-center gap-4">
            <FilterButton 
              onClick={() => setShowFilters(!showFilters)}
              active={showFilters}
            >
              <Filter className="w-5 h-5" />
              <span>Filtros</span>
            </FilterButton>

            <SortButton
              value={sortOption}
              options={sortOptions}
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </motion.div>

        {/* Filters Section */}
        <AnimatePresence>
          {showFilters && (
            <motion.div 
              className="relative backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl shadow-xl dark:shadow-black/50 p-6 mb-8 border border-white/20 dark:border-white/10 overflow-hidden"
              style={{ zIndex: 40 }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500/5 to-purple-500/5 dark:from-indigo-400/10 dark:to-purple-400/10" />
              
              <div className="relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Ubicación (común para todos los tipos) */}
                  <FilterInput label="Ubicación">
                    <div className="relative" ref={locationInputRef}>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-indigo-500 dark:text-indigo-400 w-5 h-5" />
                        <input
                          type="text"
                          value={filters.ubicacion}
                          onChange={handleLocationInputChange}
                          onFocus={() => setShowLocationSuggestions(true)}
                          placeholder="Buscar por ubicación"
                          className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-indigo-100 dark:border-indigo-800
                                   focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-700 focus:border-transparent
                                   transition-all duration-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      
                      {/* Ubicación autocompletado */}
                      {showLocationSuggestions && (
                        <div className="absolute w-full" style={{ zIndex: 50 }}>
                          <LocationSuggestions
                            query={filters.ubicacion}
                            onSelectLocation={handleLocationSelect}
                          />
                        </div>
                      )}
                    </div>
                  </FilterInput>

                  {/* Filtros específicos según el tipo de búsqueda */}
                  {searchType === 'proyectos' ? (
                    // Filtros para proyectos
                    <>
                      <FilterInput label="Estado del proyecto">
                        <div className="relative">
                          <select
                            value={filters.estadoProyecto}
                            onChange={(e) => handleFilterChange('estadoProyecto', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-indigo-100 dark:border-indigo-800
                                    focus:border-indigo-200 dark:focus:border-indigo-600 focus:outline-none
                                    transition-all duration-300 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                    [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
                          >
                            <option value="">Cualquier estado</option>
                            <option value="planos">En planos</option>
                            <option value="lanzamiento">Lanzamiento</option>
                            <option value="construccion">En construcción</option>
                            <option value="terminado">Terminado</option>
                          </select>
                          <Castle className="absolute right-4 top-1/2 transform -translate-y-1/2 
                                        text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                        </div>
                      </FilterInput>
                      
                      <FilterInput label="Dormitorios">
                        <div className="relative">
                          <select
                            value={filters.dormitorios}
                            onChange={(e) => handleFilterChange('dormitorios', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-indigo-100 dark:border-indigo-800
                                    focus:border-indigo-200 dark:focus:border-indigo-600 focus:outline-none
                                    transition-all duration-300 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                    [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
                          >
                            <option value="">Cualquier tamaño</option>
                            <option value="1">1 Dormitorio</option>
                            <option value="2">2 Dormitorios</option>
                            <option value="3">3 Dormitorios</option>
                            <option value="4">4+ Dormitorios</option>
                          </select>
                          <Bed className="absolute right-4 top-1/2 transform -translate-y-1/2 
                                   text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                        </div>
                      </FilterInput>
                    </>
                  ) : (
                    // Filtros para propiedades y alquileres
                    <>
                      <FilterInput label="Habitaciones mínimas">
                        <div className="relative">
                          <select
                            value={filters.bedrooms}
                            onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-indigo-100 dark:border-indigo-800
                                    focus:border-indigo-200 dark:focus:border-indigo-600 focus:outline-none
                                    transition-all duration-300 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                    [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
                          >
                            <option value="">Cualquiera</option>
                            {[1, 2, 3, 4, 5].map(num => (
                              <option key={num} value={num}>{num}+ Habitaciones</option>
                            ))}
                          </select>
                          <Bed className="absolute right-4 top-1/2 transform -translate-y-1/2 
                                      text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                        </div>
                      </FilterInput>

                      <FilterInput label="Baños mínimos">
                        <div className="relative">
                          <select
                            value={filters.bathrooms}
                            onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border-2 border-indigo-100 dark:border-indigo-800
                                    focus:border-indigo-200 dark:focus:border-indigo-600 focus:outline-none
                                    transition-all duration-300 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                    [&>option]:bg-white [&>option]:dark:bg-gray-800 [&>option]:text-gray-900 [&>option]:dark:text-gray-100 [&>option]:py-2 [&>option]:px-3"
                          >
                            <option value="">Cualquiera</option>
                            {[1, 2, 3, 4].map(num => (
                              <option key={num} value={num}>{num}+ Baños</option>
                            ))}
                          </select>
                          <Bath className="absolute right-4 top-1/2 transform -translate-y-1/2 
                                      text-indigo-400 dark:text-indigo-300 w-5 h-5" />
                        </div>
                      </FilterInput>
                    </>
                  )}
                </div>

                <div className="flex justify-end mt-6">
                  <motion.button
                    onClick={applyAdditionalFilters}
                    className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600
                             text-white rounded-xl shadow-lg dark:shadow-xl
                             transition-all duration-300"
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Aplicar Filtros
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading and Results Section */}
        <div className="relative results-container" style={{ zIndex: 5 }}>
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <motion.div 
              className="backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl shadow-xl dark:shadow-black/50 p-8 text-center border border-white/20 dark:border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-red-500 dark:text-red-400">{error}</p>
            </motion.div>
          ) : sortedData.length === 0 ? (
            <NoResults 
              onReset={handleResetFilters}
              searchType={getNoResultsType()}
            />
          ) : (
            <>
              {/* Grid de resultados - Diferente según el tipo de búsqueda */}
              {searchType === 'proyectos' ? (
                // Grid para proyectos (2 columnas)
                <motion.div 
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {paginatedData.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={cardAnimation}
                      whileHover="hover"
                    >
                      <PropertyCardPro 
                        item={item} 
                        isProject={true} 
                      />
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                // Grid para propiedades (3 columnas)
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {paginatedData.map((item) => (
                    <motion.div
                      key={item.id}
                      variants={cardAnimation}
                      whileHover="hover"
                    >
                      <PropertyCard property={item} />
                    </motion.div>
                  ))}
                </motion.div>
              )}
              
              {/* Pagination controls */}
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
              
              {/* Showing results info */}
              <motion.div
                className="text-center mt-6 text-gray-600 dark:text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, sortedData.length)} de {sortedData.length} {searchType === 'proyectos' ? 'proyectos' : 'propiedades'}
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;